import React, { useContext, useEffect, useRef, useState } from "react";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import Footer from "../../Footer";
import ReactQuill from "react-quill";
import _ from "lodash";
import CollapsibleCard from "./CollapsibleCard";
import CollapsibleCardItem from "./CollapsibleCardItem";
import StatusBar from "./StatusBar";
import WriteYourself from "../../svg/WriteYourself";
import WriteWithAi from "../../svg/WriteWithAi";
import axiosInstance from "../../../utils/Requests/axios";
import toast from "react-hot-toast";
import UserContext from "../../../utils/Contexts/UserContext";
import RecommendedAction from "./RecommendedAction";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Prompt from "../../../utils/hooks/usePrompt";

import "react-quill/dist/quill.snow.css";
import "../style.css";

const UpsertArticle = ({ from }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const quillRef = useRef(null);
  const previousPayloadRef = useRef(null);
  const prevLengthRef = useRef(0);
  const isInVideoPopoverRef = useRef(false);

  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);

  const [value, setValue] = useState({
    title: "",
    annotation: "",
    content: "",
  });

  const [isSave, setSave] = useState(true);
  const [activeStatusTab, setActiveStatusTab] = useState(0);
  const [contentQualityReport, setContentQualityReport] = useState({});
  const [isQualityReportActive, setIsQualityReportActive] = useState(false);
  const [isloaderForCheck, setIsloaderForCheck] = useState(false);
  const [isDraftSkipped, setIsDraftSkipped] = useState(from === "draft");
  const [forceCheckList, setForceCheckList] = useState([]);
  const [forceCheckStatus, setForceCheckStatus] = useState({});
  const [draftArticle, setDraftArticle] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { id } = useParams();
  useEffect(() => {
    let draftFromLocation = location.state?.draftArticle;
    if (draftFromLocation) {
      setDraftArticle(draftFromLocation);
    }
  }, [location]);

  const toolbarOptions = [
    [
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "code-block",
      "link",
      "image",
      "video",
      { list: "bullet" },
      { header: [2, 3, 4, 5, 6, false] },
    ],
  ];

  const videoExtensions = ["mp4", "webm", "ogg", "youtube", "vimeo", "be"];

  const isValidVideoURL = (url) => {
    try {
      // Ensure the URL has a protocol (https://)
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
      }

      const parsedUrl = new URL(url);

      const normalizedHost = parsedUrl.hostname.replace(/^www\./, "");

      if (normalizedHost === "youtube.com" || normalizedHost === "youtu.be") {
        const regex =
          /(?:youtu.be\/|youtube.com\/(?:[^/]+\/.*\/|(?:v|e(?:mbed)?)\/|.*[?&]v=))([^"&?/\\s]{11})/;
        return regex.test(url);
      }

      // Check for allowed video extensions in the URL's pathname
      const pathExtension = parsedUrl.pathname.split(".").pop();
      if (videoExtensions.includes(pathExtension)) {
        return true;
      }

      // Check for Vimeo or other video platforms by domain name
      if (normalizedHost === "vimeo.com" || normalizedHost === "be.com") {
        return true; // You can refine this further if needed based on specific URL patterns.
      }

      // Ensure that the URL is a video URL, not a general website like Wikipedia
      // This prevents other invalid URLs (like Wikipedia or general websites) from passing.
      const nonVideoHosts = [
        "wikipedia.org",
        "twitter.com",
        "facebook.com",
        "instagram.com",
      ];
      if (nonVideoHosts.includes(normalizedHost)) {
        return false; // Return false for non-video hosts
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const videoHandler = function () {
        const tooltip = this.quill.theme.tooltip;
        tooltip.edit("video");

        isInVideoPopoverRef.current = true;

        const input = tooltip.root.querySelector("input");
        input.addEventListener("change", function (event) {
          const url = event.target.value;
          if (isInVideoPopoverRef.current && isValidVideoURL(url) === false) {
            toast.error(t("There was an error uploading file."));
            event.target.value = "";
            isInVideoPopoverRef.current = false; // Reset the flag
            alert(t("There was an error uploading file."));
          }
        });
      };

      // Attach the video handler to the toolbar
      quill.getModule("toolbar").handlers.video = videoHandler;
    }

    setSave(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValue((prevValue) => ({
      ...prevValue,
      [name]: value,
    }));
    if (value?.content !== "") {
      setSave(false);
    }
  };

  const handleEditorChange = (editorContent) => {
    setValue((prevValue) => ({
      ...prevValue,
      content: editorContent,
    }));
    // added condition as on intial load, the editor trigger handleChange, so the state becomes false
    // causes alert
    if (value?.content !== "") {
      if (value?.content !== editorContent) {
        setSave(false);
      }
    } else {
      setSave(true);
    }
  };

  const sanitizeLinks = (content) => {
    const linkRegex = /<a\s+[^>]*href="([^"]*)"[^>]*>/g;
    return content.replace(linkRegex, (match, href) => {
      if (
        href &&
        !href.startsWith("http://") &&
        !href.startsWith("https://") &&
        !href.startsWith("www")
      ) {
        // Here, we assume a base URL needs to be prepended (adjust this as needed)
        const fixedHref = `https://${href}`; // Adjust base URL as necessary
        return match.replace(href, fixedHref);
      }
      return match;
    });
  };

  const handleStatusTabClick = (relValue) => {
    setActiveStatusTab(relValue);
  };

  const contentTypes = [
    { value: 0, name: t("Write Yourself"), icon: <WriteYourself /> },
    { value: 1, name: t("Write with AI"), icon: <WriteWithAi /> },
  ];

  const handleSubmitForNew = (e) => {
    e.preventDefault();
    if (!value?.title || !value?.content) {
      toast.error(t("Please fill in all the required fields"));
    } else {
      setIsSubmitting(true);
      const payload = _.cloneDeep(value);
      const url = `users/${userData?.uuid}/articles`;
      axiosInstance
        .post(url, payload)
        .then((response) => {
          setIsSubmitting(false);
          navigate("/content/articles");
        })
        .catch((err) => {
          if (err?.data?.message?.includes("title_field should not be empty")) {
            toast.error(t("Title field should not be empty"));
            setIsSubmitting(false);
          } else {
            toast.error(t("There was an error fetching response"));
            setIsSubmitting(false);
          }
        });
    }
    setSave(true);
  };

  const handleSubmitForDraft = (e) => {
    e.preventDefault();
    if (!value?.title || !value?.content) {
      toast.error(t("Please fill in all the required fields"));
    } else {
      setIsSubmitting(true);
      const payload = _.cloneDeep(value);
      const url = `users/${userData?.uuid}/articles/${draftArticle?.data?.uuid}`;
      axiosInstance
        .put(url, payload)
        .then((response) => {
          setIsSubmitting(false);
          navigate("/content/articles");
        })
        .catch((err) => {
          if (err?.data?.message?.includes("title_field should not be empty")) {
            toast.error(t("Title field should not be empty"));
            setIsSubmitting(false);
          } else {
            toast.error(t("There was an error fetching response"));
            setIsSubmitting(false);
          }
        });
    }
    setSave(true);
  };

  const handleQualityCheck = () => {
    setIsQualityReportActive(null);
    setIsloaderForCheck(true);
    const payload = _.cloneDeep(value);
    const url = `articles/quality`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        setIsQualityReportActive(false);
        setIsloaderForCheck(false);
        const updatedPlagiarism =
          response?.data?.plagiarism !== null
            ? response?.data?.plagiarism
            : {
                pass: contentQualityReport?.plagiarism?.pass,
                passed: contentQualityReport?.plagiarism?.passed,
              };
        const updatedAi =
          response?.data?.ai !== null
            ? response?.data?.ai
            : {
                pass: contentQualityReport?.ai?.pass,
                passed: contentQualityReport?.ai?.passed,
              };
        setContentQualityReport({
          ...response?.data,
          plagiarism: updatedPlagiarism,
          ai: updatedAi,
          ...forceCheckStatus,
          checks: [
            ...(forceCheckList || []),
            ...(response?.data?.checks || []),
          ],
        });
      })
      .catch((err) => {
        setIsloaderForCheck(false);
        if (err?.data?.message?.includes("title_field should not be empty")) {
          toast.error(t("Title field should not be empty"));
        } else {
          toast.error(t("There was an error fetching response"));
        }
      });
  };

  const getPlainTextLength = () => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const plainText = quill.getText();
      return plainText.trim().length;
    }
    return 0;
  };

  const handleQualityCheckForDraft = () => {
    setIsQualityReportActive(null);
    const url = `users/${userData?.uuid}/articles/${id}/quality`;
    axiosInstance
      .get(url)
      .then((response) => {
        setIsQualityReportActive(false);
        setContentQualityReport({
          ...response?.data,
          ...forceCheckStatus,
          checks: [
            ...(forceCheckList || []),
            ...(response?.data?.checks || []),
          ],
        });
        setIsloaderForCheck(false);
      })
      .catch(() => {
        setIsloaderForCheck(false);
        toast.error(t("There was an error fetching response"));
      });
  };

  const getContent = () => {
    const url = `users/${userData?.uuid}/articles/${id}/content`;
    axiosInstance
      .get(url)
      .then((response) => {
        setValue((prevValue) => ({
          ...prevValue,
          content: response?.data?.content,
        }));
      })
      .catch(() => {
        toast.error(t("There was an error fetching response"));
      });
  };

  const getTitle = () => {
    const url = `users/${userData?.uuid}/articles/${id}`;
    axiosInstance
      .get(url)
      .then((response) => {
        setValue((prevValue) => ({
          ...prevValue,
          title: response?.data?.title,
          annotation: response?.data?.annotation,
        }));
      })
      .catch(() => {
        toast.error(t("There was an error fetching response"));
      });
  };

  useEffect(() => {
    const payload = { ...draftArticle };
    if (!_.isEqual(previousPayloadRef.current, payload) && from === "draft") {
      previousPayloadRef.current = payload;
      if (id) {
        handleQualityCheckForDraft();
        getContent();
        getTitle();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftArticle, from]);

  useEffect(() => {
    const debouncedDescription = _.debounce(() => {
      handleQualityCheck();
    }, 500);

    const currentLength = getPlainTextLength();
    const titleLength = value?.title?.length;

    if (currentLength < 100) {
      prevLengthRef.current = 0;
    } else if (
      (currentLength >= 100 && prevLengthRef.current === 0) ||
      (currentLength >= 100 &&
        titleLength >= 6 &&
        Math.abs(currentLength - prevLengthRef.current) >= 6)
    ) {
      if (isDraftSkipped) {
        setTimeout(() => {
          setIsDraftSkipped(false);
        }, 2000);
      } else {
        debouncedDescription();
      }
      prevLengthRef.current = currentLength;
    }

    return () => {
      debouncedDescription.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.content, value.title, handleQualityCheck]);

  const QualityCheck = [
    {
      id: "word-count",
      title: t("Words Count"),
      pass:
        contentQualityReport?.words == null
          ? null
          : contentQualityReport.words >= 607,
      tag: `${contentQualityReport?.words ?? 0} ${t("Words")}`,
      description: t(
        "To meet the requirements of more sellers, we recommend keeping your content within the average range specified across all offers: a minimum of 607 words and a maximum of 4,560 words"
      ),
      type: 9,
    },
    {
      id: "plagiarism",
      title: t("Plagiarism"),
      pass: contentQualityReport?.plagiarism?.passed ?? null,
      tag: `${
        contentQualityReport?.plagiarism?.passed === null ||
        contentQualityReport?.plagiarism === null
          ? t("Not Checked")
          : t("Detected")
      }`,
      description: t(
        "Approximately 90% of sellers require content that is unique and has never been published before. To increase your publication opportunities, we recommend keeping plagiarized (or similar to already published) content below 15%"
      ),
      type: 2,
      recommended: true,
      forceCheckType: 51,
    },
    {
      id: "readability",
      title: t("Readability"),
      pass: contentQualityReport?.readability?.passed ?? null,
      tag: `${
        contentQualityReport?.readability?.passed
          ? t("Fairly Easy")
          : t("Not Detected")
      }`,
      description: t(
        "For optimal SEO, aim for a Flesch Reading Ease score between 60 and 70, equivalent to an 8th-grade reading level. This balance ensures your content is easily understood by a broad audience while effectively conveying valuable information"
      ),
      type: 4,
    },
    {
      id: "seo",
      title: t("SEO"),
      pass: contentQualityReport?.seo?.passed ?? null,
      tag: `${
        contentQualityReport?.seo?.passed ? t("Optimized") : t("Not Detected")
      }`,
      description: t(
        "Proper SEO formatting is essential as it helps search engine crawlers understand the structure and content of a webpage, improving its relevance to specific search queries and influencing its ranking on search engine results pages (SERPs)"
      ),
      type: 3,
    },
    {
      id: "image",
      title: t("Image"),
      pass: contentQualityReport?.image?.passed ?? null,
      tag: `${contentQualityReport?.images ?? 0} ${t("Image")}`,
      description: t(
        "Around 30% of sellers require content that includes at least one unique image. We recommend incorporating images as they greatly enhance SEO by making your content more engaging, accessible, and visually appealing to users. Properly optimized and indexed images can also improve search engine rankings"
      ),
      type: 1,
    },
    {
      id: "ai-generated-content",
      title: t("AI Generated Content"),
      pass: contentQualityReport?.ai?.passed ?? null,
      tag: `${
        contentQualityReport?.ai?.passed === null ||
        contentQualityReport?.ai === null
          ? t("Not Checked")
          : t("Detected")
      }`,
      description: t(
        "Around 30% of sellers require content that includes at least one unique image. We recommend incorporating images as they greatly enhance SEO by making your content more engaging, accessible, and visually appealing to users. Properly optimized and indexed images can also improve search engine rankings"
      ),
      type: 5,
      recommended: true,
      forceCheckType: 52,
    },
  ];

  const adjustTooltipPosition = () => {
    setTimeout(() => {
      const tooltip = document.querySelector(".ql-tooltip");
      if (tooltip) {
        const rect = tooltip.getBoundingClientRect();
        const screenWidth = window.innerWidth;

        if (rect.right > screenWidth) {
          tooltip.style.left = "auto";
          tooltip.style.right = "0";
          tooltip.style.transform = "translateX(-100%)";
        } else if (rect.left < 0) {
          tooltip.style.left = "0";
          tooltip.style.right = "auto";
          tooltip.style.transform = "translateX(0%)";
        } else {
          tooltip.style.left = "";
          tooltip.style.right = "";
          tooltip.style.transform = "";
        }
      }
    }, 100);
  };

  useEffect(() => {
    document.addEventListener("click", adjustTooltipPosition);
    return () => document.removeEventListener("click", adjustTooltipPosition);
  }, []);

  Prompt({
    isDirty: !isSave,
  });

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div
          className="page-header d-flex justify-content-between align-items-center"
          style={{ paddingBottom: "1em" }}
        >
          <div className="row align-items-center mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  {/* eslint-disable-next-line */}
                  <a href="javascript:;"
                    className="breadcrumb-link"
                    style={{ opacity: "0.6" }}
                  >
                    {t("Creating an article")}
                  </a>
                </li>
              </ol>
            </nav>
            <div className="col-md d-md-flex justify-content-between align-items-center mb-sm-2 mb-2 mb-md-0">
              <h1 className="page-header-title">{t("Content Editor")}</h1>
            </div>
          </div>
          <button
            disabled={isSubmitting}
            onClick={(e) =>
              from === "new" ? handleSubmitForNew(e) : handleSubmitForDraft(e)
            }
            className="btn btn-primary d-flex justify-content-center align-items-center gap-2"
            style={{
              borderRadius: "10px",
              width: "152px",
              fontWeight: "600",
              fontSize: "16px",
              height: "47px",
            }}
          >
            {from === "new" ? t("Submit") : t("Save")}
          </button>
        </div>
        <div className="quill-editor-wrapper">
          <div className="left-container">
            <div className="card">
              <div className="card-header">
                <ul
                  className="nav nav-segment parent-width-full"
                  role="tablist"
                >
                  {contentTypes?.map((status) => {
                    return (
                      <li
                        className="nav-item text-center quill-button-dynamic-width"
                        key={status.value}
                      >
                        <a
                          className={`js-loading-state-invoker nav-link write-tabs ${
                            activeStatusTab === status.value
                              ? "active active-icon"
                              : ""
                          }`}
                          id="nav-one-eg1-tab"
                          data-bs-toggle="pill"
                          href="#nav-one-eg1"
                          role="tab"
                          aria-controls="nav-one-eg1"
                          aria-selected={activeStatusTab === status.value}
                          onClick={(e) => {
                            e.preventDefault();
                            handleStatusTabClick(status.value);
                          }}
                          data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                        >
                          {status.icon} <span>{status.name}</span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div
                className="d-flex flex-column"
                style={{ padding: "16px 21px", gap: "21px" }}
              >
                <div>
                  <label
                    htmlFor="ArticleTitle"
                    className="form-label d-flex justify-content-between"
                    style={{ color: "var(--lightWhite)" }}
                  >
                    <span>{t("Article Title")}</span>
                    {/* might be use in future  */}
                    {/* <span>{value?.title?.length}/80</span> */}
                  </label>
                  <input
                    type="text"
                    name="title"
                    className="js-input-mask form-control custom-placeholder"
                    id="ArticleTitle"
                    placeholder="Lorem Ipsum..."
                    // maxLength="80"
                    value={value?.title}
                    onChange={(e) => handleChange(e)}
                  />
                </div>
                {/* might be use in future  */}
                {/* <div>
                  <label htmlFor="ArticleAbstract" className="form-label d-flex justify-content-between" style={{ color: 'var(--lightWhite)' }}>
                    <span>Article Abstract </span> <span>{value?.annotation?.length}/600</span>
                  </label>
                  <textarea
                    className="js-input-mask form-control text-secondary"
                    id="ArticleAbstract"
                    name="annotation"
                    placeholder="Lorem Ipsum..."
                    maxLength="600"
                    rows="5"
                    value={value?.annotation}
                    onChange={handleChange}
                  />
                </div> */}
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  name="content"
                  className="form-control"
                  value={sanitizeLinks(value?.content)}
                  onChange={handleEditorChange}
                  modules={{
                    toolbar: toolbarOptions,
                    clipboard: {
                      matchVisual: false,
                    },
                  }}
                  placeholder={`${t("Type your description")}...`}
                />
              </div>
            </div>
          </div>
          <div className="right-container">
            <CollapsibleCard
              id="quality_report"
              title={`${t("Content Quality Report")}`}
              status={isQualityReportActive}
              renderNode={
                <>
                  {QualityCheck?.map((item) => {
                    const {
                      id,
                      title,
                      pass,
                      tag,
                      description,
                      type,
                      recommended,
                      forceCheckType,
                    } = item;
                    return (
                      <>
                        <CollapsibleCardItem
                          id={id}
                          title={title}
                          pass={pass}
                          tag={tag}
                          description={description}
                          loading={isloaderForCheck}
                          renderNode={
                            <>
                              {contentQualityReport?.checks
                                ?.filter((check) => check?.type === type)
                                ?.map((data) => {
                                  return (
                                    <StatusBar
                                      title={data?.message}
                                      pass={data?.solved}
                                    />
                                  );
                                })}
                              {recommended && (
                                <RecommendedAction
                                  from={from}
                                  pass={pass}
                                  value={{
                                    type: forceCheckType,
                                    article: value,
                                    updateType: type,
                                  }}
                                  setContentQualityReport={
                                    setContentQualityReport
                                  }
                                  contentQualityReport={contentQualityReport}
                                  articalUuid={draftArticle?.data?.uuid}
                                  setForceCheckList={setForceCheckList}
                                  setForceCheckStatus={setForceCheckStatus}
                                />
                              )}
                            </>
                          }
                        />
                      </>
                    );
                  })}
                </>
              }
            />
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
};

export default UpsertArticle;
